import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Modal } from 'react-bootstrap';
import './confirm-dialog.css';

const ConfirmContext = createContext(null);

const DEFAULT_OPTIONS = {
  title: 'Potrditev',
  message: '',
  confirmLabel: 'Potrdi',
  cancelLabel: 'Prekliči',
  variant: 'danger',
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ show: false, options: DEFAULT_OPTIONS });
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        show: true,
        options: { ...DEFAULT_OPTIONS, ...options },
      });
    });
  }, []);

  const close = useCallback((result) => {
    setState((prev) => ({ ...prev, show: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const { title, message, confirmLabel, cancelLabel, variant } = state.options;
  const confirmVariant = variant === 'danger' ? 'danger' : 'primary';

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Modal
        show={state.show}
        onHide={() => close(false)}
        centered
        dialogClassName="platform-confirm-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{message}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => close(false)}>
            {cancelLabel}
          </Button>
          <Button variant={confirmVariant} onClick={() => close(true)}>
            {confirmLabel}
          </Button>
        </Modal.Footer>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return context;
}
